/**
* Nom : trialDetails.js
* Description : Ce fichier contient la définition de la page trialDetails 
*               Cette page affiche les informations détaillées d'un essai
* Date de création : 27 septembre 2024
*/

/* CSS et assets */
import { EditIcon } from 'package_ui_ihm/dist'
import { DeleteIcon } from 'package_ui_ihm/dist'
import { AddIcon } from 'package_ui_ihm/dist'
import { RemoveIcon } from 'package_ui_ihm/dist'
import { HelpIcon } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import Tooltip from '@mui/material/Tooltip';
import { TabRD } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { Table } from 'package_ui_ihm/dist'
import { TableStatus } from 'package_ui_ihm/dist'
import { BackPage } from 'package_ui_ihm/dist'
import { Modality } from 'package_ui_ihm/dist'
import { TextareaInput } from 'package_ui_ihm/dist'
import { Link } from 'react-router-dom'

/* Hook */
import { useState, useEffect } from 'react';
import { useParams, useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useData } from 'package_ui_ihm/dist';
import { useScreenWidth } from 'package_ui_ihm/dist'


/**
* TrialDetails : affiche la page qui contient les informations détaillées d'un essai
*                    plusieurs liens permettent de naviguer sur d'autres pages
*
* Type : page 
*
* Parametres : 
*  None
*/
function TrialDetails() {

  //Define useParam
  const { id } = useParams()


  // Define useState hooks
  const [trial, setTrial] = useState(null)
  const [trialValidationStatus, setTrialValidationStatus] = useState(null)
  const [statusTrial, setStatusTrial] = useState(null)
  const [modalities, setModalities] = useState([])
  const [modalityAdd, setModalityAdd] = useState(false)
  const [modalitySubmit, setModalitySubmit] = useState(false)
  const [modalityReference, setModalityReference] = useState("")
  const [selectedValidationStatus, setSelectedValidationStatus] = useState(0)
  const [modalityFrequenceApplication, setModalityFrequenceApplication] = useState(-1)
  const [modalityComment, setModalityComment] = useState("")
  const [modalityReferenceError, setModalityReferenceError] = useState(false)
  const [validationStatusError, setValidationStatusError] = useState(false)
  const [modalityFrequenceApplicationError, setModalityFrequenceApplicationError] = useState(false)
  const [articles, setArticles] = useState(null)
  const [articleAdd, setArticleAdd] = useState(false)
  const [articleRemove, setArticleRemove] = useState(false)
  const [articleList, setArticleList] = useState(null)
  const [selectedArticleAdd, setSelectedArticleAdd] = useState(0)
  const [selectedArticleRemove, setSelectedArticleRemove] = useState(0)
  const [reports, setReports] = useState(null)
  const [reportAdd, setReportAdd] = useState(false)
  const [reportRemove, setReportRemove] = useState(false)
  const [reportList, setReportList] = useState(null)
  const [selectedReportAdd, setSelectedReportAdd] = useState(0)
  const [selectedReportRemove, setSelectedReportRemove] = useState(0)
  const [isDataLoading, setDataLoading] = useState(false)
  const [repeatUpdate, setRepeatUpdate] = useState(false)
  const [noteIntervalleRecurrence, setNoteIntervalleRecurrence] = useState(null)
  const [commentUpdate, setCommentUpdate] = useState(false)
  const [comment, setComment] = useState(null)
  const [keywordsUpdate, setKeywordsUpdate] = useState(false)
  const [keywords, setKeywords] = useState(null)
  const [locationUpdate, setLocationUpdate] = useState(false)
  const [location, setLocation] = useState(null)
  const [ambientTemperatureUpdate, setAmbientTemperatureUpdate] = useState(false)
  const [ambientTemperature, setAmbientTemperature] = useState(null)
  const [ambientHumidityUpdate, setAmbientHumidityUpdate] = useState(false)
  const [ambientHumidity, setAmbientHumidity] = useState(null)
  const [preservationConditionUpdate, setPreservationConditionUpdate] = useState(false)
  const [preservationCondition, setPreservationCondition] = useState(null)
  const [modalityBlock, setModalityBlock] = useState(false);


  // Define useContext hooks
  const { authStatus, authToken, currentUser } = useAuth();
  const { path } = useApi();
  const { validationStatus } = useData();


  // Define useBlocker hooks
  const shouldBlock = 
    modalityAdd || 
    repeatUpdate || 
    locationUpdate || 
    ambientTemperatureUpdate || 
    ambientHumidityUpdate ||
    preservationConditionUpdate ||
    commentUpdate ||
    keywordsUpdate ||
    articleAdd ||
    reportAdd ||
    modalityBlock;
  const blocker = useBlocker(shouldBlock);


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();


  // Define functions
  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
      accept: 'application/json',
      Authorization: token,
    }
    // Requests in parallel to win time 
    const [resTrial, resModalities, resArticles, resAllArticles, resReports, resAllReports] = await Promise.all([
        // Trial details
        fetch(path + "/api/bo-tral/" + id + "/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Modalities linked to trial
        fetch(path + "/api/bo-moty/?trial_id=" + id, {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Scientific articles linked to trial
        fetch(path + "/api/bo-ltsa/?trial_id=" + id, {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Scientific articles - whole list
        fetch(path + "/api/bo-scar/filter-list/" , {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // AT reports linked to trial
        fetch(path + "/api/bo-ltar/?trial_id=" + id, {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // AT reports  - whole list
        fetch(path + "/api/bo-atre", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
    ])
    // Responses in parallel to win time
    const [dataTrial, dataModalities, dataArticles, dataAllArticles, dataReports, dataAllReports] = await Promise.all([
        resTrial.json(),
        resModalities.json(),
        resArticles.json(),
        resAllArticles.json(),
        resReports.json(),
        resAllReports.json(),
    ])
    // Set variables


    // Retrieve trial details
    setStatusTrial(resTrial["status"])
    setTrial(dataTrial)
    setTrialValidationStatus(dataTrial['validation_status'])
    setModalities(dataModalities["results"])
    setArticles(dataArticles["results"])
    setArticleList(dataAllArticles)
    setReports(dataReports["results"])
    setReportList(dataAllReports["results"])
    // Dataloading
    setDataLoading(false)
  }

  async function updateTrialStatus(new_status_validation_id) {
    // Token
    const token = "Bearer " + authToken
    // Update trial
    await fetch(path + "/api/bo-tral/" + id + "/change-status/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          "new_status_validation_id": new_status_validation_id
        })
      }
    );
  }

  const handleClickModalityAdd = (event) => {
    event.preventDefault();
    if(modalityAdd === false) {
      setModalityAdd(true)
    } else {
      setModalityAdd(false)
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if(modalityReference === null || modalityReference === ''){
      setModalityReferenceError(true)
    }
    else
    {   
      setModalityReferenceError(false)
    }
    if(selectedValidationStatus === 0 || selectedValidationStatus === '0'){
      setValidationStatusError(true)
    }
    else
    {   
      setValidationStatusError(false)
    }
    if(modalityFrequenceApplication === -1){
      setModalityFrequenceApplicationError(true)
    }
    else
    {   
      setModalityFrequenceApplicationError(false)
    }
    setModalitySubmit(true)
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setModalityAdd(false)
  };

  async function sendData(trial_id, reference, comments, validation_status_id, application_frequence) {
    // Token
    const token = "Bearer " + authToken
    // Création du JSON
    var json_data
    if(comment === ""){
      json_data = JSON.stringify({
                    "trial_id": trial_id, 
                    "reference": reference,
                    "validation_status_id": validation_status_id,
                    "application_frequence": application_frequence
                  })
    } else {
      json_data = JSON.stringify({
                    "trial_id": trial_id, 
                    "reference": reference,
                    "validation_status_id": validation_status_id,
                    "comments": comments,
                    "application_frequence": application_frequence
                  })
    }
    // create modality
    await fetch(path + "/api/bo-moty/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: json_data
      }
    );
    setModalitySubmit(false)
    retrieveData()
    setModalityAdd(false)
    setSelectedValidationStatus(0)
    setModalityReference("")
    setModalityComment("")
    setModalityFrequenceApplication(-1)
  }

  const handleRemove = (event, modality_id) => {
    event.preventDefault();
    removeData(modality_id)
  };

  async function removeData(item_id) {
    // Token
    const token = "Bearer " + authToken
    // delete modality
    await fetch(path + "/api/bo-moty/" + item_id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: token,
        }
      }
    );
    retrieveData()
  }

  const handleClickArticleAdd = (event) => {
    event.preventDefault();
    if(articleAdd === false) {
      setArticleAdd(true)
    } else {
      setArticleAdd(false)
    }
  };

  const handleClickArticleRemove = (event) => {
    event.preventDefault();
    if(articleRemove === false) {
      setArticleRemove(true)
    } else {
      setArticleRemove(false)
    }
  };

  const handleClickReportAdd = (event) => {
    event.preventDefault();
    if(reportAdd === false) {
      setReportAdd(true)
    } else {
      setSelectedArticleAdd(0)
      setReportAdd(false)
    }
  };

  const handleClickReportRemove = (event) => {
    event.preventDefault();
    if(reportRemove === false) {
      setReportRemove(true)
    } else {
      setSelectedArticleRemove(0)
      setReportRemove(false)
    }
  };

  async function sendArticle(trial_id, article_id) {
    // Token
    const token = "Bearer " + authToken
    // Create link trial article
    await fetch(path + "/api/bo-ltsa/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "trial_id": trial_id, 
            "scientific_article_id": article_id,
        })
      }
    );
    retrieveData()
    setArticleAdd(false)
    setSelectedArticleAdd(0)
  }

  async function sendDeleteArticle(item_id) {
    // Token
    const token = "Bearer " + authToken
    // Delete link trial article
    await fetch(path + "/api/bo-ltsa/" + item_id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: token,
        }
      }
    );
    retrieveData()
    setArticleRemove(false)
    setSelectedArticleRemove(0)
  }

  async function sendReport(trial_id, report_id) {
    // Token
    const token = "Bearer " + authToken
    // Create link trial report
    await fetch(path + "/api/bo-ltar/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "trial_id": trial_id, 
            "at_report_id": report_id,
        })
      }
    );
    retrieveData()
    setReportAdd(false)
    setSelectedReportAdd(0)
  }

  async function sendDeleteReport(item_id) {
    // Token
    const token = "Bearer " + authToken
    // Delete link trial report
    await fetch(path + "/api/bo-ltar/" + item_id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        }
      }
    );
    retrieveData()
    setReportRemove(false)
    setSelectedReportRemove(0)
  }
  
  const repeatEditIcon = (event) => {
    event.preventDefault();
    setNoteIntervalleRecurrence(trial["note_intervalle_recurrence"])
    setRepeatUpdate(true)
  };
  
  const repeatUpdateSubmit = (event) => {
    event.preventDefault();
    sendRepeatUpdate(noteIntervalleRecurrence)
  };

  const handleKeyDown = (event) => {
    // Si la touche Entrée est pressée, empêche la validation du formulaire
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
    }
  };

  const repeatUpdateReset = (event) => {
    event.preventDefault();
    setNoteIntervalleRecurrence(null)
    setRepeatUpdate(false)
  };

  async function sendRepeatUpdate(note_intervalle_recurrence) {
    // Token
    const token = "Bearer " + authToken
    // update note_intervalle_recurrence
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "note_intervalle_recurrence": note_intervalle_recurrence
        })
      }
    );
    retrieveData()
    setRepeatUpdate(false)
  }

  const locationEditIcon = (event) => {
    event.preventDefault();
    setLocation(trial["location"])
    setLocationUpdate(true)
  };
  
  const locationUpdateSubmit = (event) => {
    event.preventDefault();
    sendLocationUpdate(location)
  };

  const locationUpdateReset = (event) => {
    event.preventDefault();
    setLocation(null)
    setLocationUpdate(false)
  };

  async function sendLocationUpdate(location) {
    // Token
    const token = "Bearer " + authToken
    // update note_intervalle_recurrence
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "location": location
        })
      }
    );
    retrieveData()
    setLocationUpdate(false)
  }

  const ambientTemperatureEditIcon = (event) => {
    event.preventDefault();
    setAmbientTemperature(trial["ambient_temperature"])
    setAmbientTemperatureUpdate(true)
  };
  
  const ambientTemperatureUpdateSubmit = (event) => {
    event.preventDefault();
    sendAmbientTemperatureUpdate(ambientTemperature)
  };

  const ambientTemperatureUpdateReset = (event) => {
    event.preventDefault();
    setAmbientTemperature(null)
    setAmbientTemperatureUpdate(false)
  };

  async function sendAmbientTemperatureUpdate(ambient_temperature) {
    // Token
    const token = "Bearer " + authToken
    // update note_intervalle_recurrence
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "ambient_temperature": ambient_temperature
        })
      }
    );
    retrieveData()
    setAmbientTemperatureUpdate(false)
  }

  const ambientHumidityEditIcon = (event) => {
    event.preventDefault();
    setAmbientHumidity(trial["ambient_humidity"])
    setAmbientHumidityUpdate(true)
  };
  
  const ambientHumidityUpdateSubmit = (event) => {
    event.preventDefault();
    sendAmbientHumidityUpdate(ambientHumidity)
  };

  const ambientHumidityUpdateReset = (event) => {
    event.preventDefault();
    setAmbientHumidity(null)
    setAmbientHumidityUpdate(false)
  };

  async function sendAmbientHumidityUpdate(ambient_humidity) {
    // Token
    const token = "Bearer " + authToken
    // update note_intervalle_recurrence
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "ambient_humidity": ambient_humidity
        })
      }
    );
    retrieveData()
    setAmbientHumidityUpdate(false)
  }

  const preservationConditionEditIcon = (event) => {
    event.preventDefault();
    setPreservationCondition(trial["preservation_condition"])
    setPreservationConditionUpdate(true)
  };
  
  const preservationConditionUpdateSubmit = (event) => {
    event.preventDefault();
    sendPreservationConditionUpdate(preservationCondition)
  };

  const preservationConditionUpdateReset = (event) => {
    event.preventDefault();
    setPreservationCondition(null)
    setPreservationConditionUpdate(false)
  };

  async function sendPreservationConditionUpdate(preservation_condition) {
    // Token
    const token = "Bearer " + authToken
    // update note_intervalle_recurrence
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "preservation_condition": preservation_condition
        })
      }
    );
    retrieveData()
    setPreservationConditionUpdate(false)
  }
  
  const commentEditIcon = (event) => {
    event.preventDefault();
    setComment(trial["comments"])
    setCommentUpdate(true)
  };
  
  const commentUpdateSubmit = (event) => {
    event.preventDefault();
    sendCommentUpdate(comment)
  };

  const commentUpdateReset = (event) => {
    event.preventDefault();
    setComment(null)
    setCommentUpdate(false)
  };

  async function sendCommentUpdate(comment) {
    // Token
    const token = "Bearer " + authToken
    // update comment
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "comments": comment
        })
      }
    );
    retrieveData()
    setCommentUpdate(false)
  }
  
  const keywordsEditIcon = (event) => {
    event.preventDefault();
    setKeywords(trial["keywords"])
    setKeywordsUpdate(true)
  };
  
  const keywordsUpdateSubmit = (event) => {
    event.preventDefault();
    sendKeywordsUpdate(keywords)
  };

  const keywordsUpdateReset = (event) => {
    event.preventDefault();
    setKeywords(null)
    setKeywordsUpdate(false)
  };

  async function sendKeywordsUpdate(keywords) {
    // Token
    const token = "Bearer " + authToken
    // update keywords
    await fetch(path + "/api/bo-tral/" + id + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "keywords": keywords
        })
      }
    );
    retrieveData()
    setKeywordsUpdate(false)
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    var new_status_validation = trialValidationStatus
    var new_status_validation_id = 4
    for (const modality of modalities) {
      if( new_status_validation_id > modality.validation_status.id){
        new_status_validation = modality.validation_status
        new_status_validation_id = modality.validation_status.id
      }
    }
    updateTrialStatus(new_status_validation_id)
    setTrialValidationStatus(new_status_validation)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalities])

  useEffect(() => {
    if(modalitySubmit === true && modalityReferenceError === false && validationStatusError === false && modalityFrequenceApplicationError === false){
      sendData(id, modalityReference, modalityComment, selectedValidationStatus, modalityFrequenceApplication)
    } 
    else
    {
      setModalitySubmit(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalitySubmit])

  useEffect(() => {
    if(selectedArticleAdd !== 0)
    {
      sendArticle(id, selectedArticleAdd)
    } 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArticleAdd])

  useEffect(() => {
    if(selectedArticleRemove !== 0)
    {
      sendDeleteArticle(selectedArticleRemove)
    } 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArticleRemove])

  useEffect(() => {
    if(selectedReportAdd !== 0)
    {
      sendReport(id, selectedReportAdd)
    } 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportAdd])

  useEffect(() => {
    if(selectedReportRemove !== 0)
    {
      sendDeleteReport(selectedReportRemove)
    } 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportRemove])

  useEffect(() => {
    retrieveData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalityBlock])

  useEffect(() => {
    // Bloquer la fermeture d'onglet/rafraîchissement
    const handleBeforeUnload = (e) => {
      if (shouldBlock === true) {
        // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
        e.preventDefault();
        e.returnValue = "";
        // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
        return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
      }
    }; 

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Nettoyer l'écouteur quand le composant est démonté
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock]);

  useEffect(() => {
    if (blocker.state === "blocked") {
        const confirmed = window.confirm(
            "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
        );

        if (confirmed) {
            blocker.proceed();
        } else {
            blocker.reset();
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);

  // Render component
  return (
    <div>
      <TabRD tabNumber={2}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <div><p>Chargement des données</p></div>
          ) : (
            (statusTrial === 200) ? (
              <div>
                {/*Titre*/}
                <div className={(screenWidth > 650) ? 'flex-row' : null}>
                  <h1>{trial["name"]}</h1>
                  <div className='flex-row'>
                    <Link to={'/trials/' + trial["id"] + '/edit'} className='link link-icon'>
                          <img src={EditIcon} alt='edit_item_icon' className='link-img' />
                    </Link>
                    { (currentUser["role_name"] === "AT_ADMIN") ? (
                        <Link to={'/trials/' + trial["id"] + '/delete'} className='link link-icon'>
                            <img src={DeleteIcon} alt='delete_item_icon' className='link-img' />
                        </Link>
                      ) : (
                        null
                      )
                    }
                  </div>
                </div>
                {/*Lien de retour*/}
                <div className='flex-row justify-start margin-left margin-bottom margin-top'>
                    <div className='link-border'>
                        <BackPage path='/trials'>Retour à la liste des essais</BackPage>
                    </div>
                </div>
                {/*Info essai*/}
                <TableStatus label={'Status'} status={trialValidationStatus["name"]} color={trialValidationStatus["color"]}/>
                <div className='grid-1-1'>
                  <Table labels={['Cible', "Plage d'hormèse", 'Type de traitement', 'Date', 'Réference', 'Produit']} datas={[trial["target"]["name"], trial["target"]["hormesis_min"] + " mJ/cm² - " + trial["target"]["hormesis_max"] + " mJ/cm²", trial["treatment_type"]["name"], trial["month"]["name"] + '-' + trial["year"], trial["reference"], trial["product"]['name'] + ' V' + trial["product_version"]]}/>
                </div>
                {/*Liste des modalités de l'essai*/}
                <div className='margin-bottom margin-top'>
                  <div className='link'>
                    <h3 className='table-cell table-label'>Modalités  </h3>
                    <img src={AddIcon} alt='add_item_icon' onClick={handleClickModalityAdd} className='link-img link-icon' />
                  </div>
                  {(modalityAdd === true) ? (
                    <div key={0} className='modality-border grid-1-10-1'>
                      <div className='margin-right'>
                        <p className='modality-subtitle'>Référence : </p>
                        <input className={(modalityReferenceError === false) ? 'form-input' : 'form-input-error'} type='text' placeholder='Entrer une référence' value={modalityReference} onChange={(e) => setModalityReference(e.target.value)}/>
                        <p className='modality-subtitle'>Status : </p>
                        <label className='select-form-tr'>
                          <select name='selectedChoice' className={(validationStatusError === false) ? 'select-form-option' : 'select-form-error'} value={selectedValidationStatus} onChange={e => setSelectedValidationStatus(e.target.value)}>
                            <option value={0}>{"--Selectionnez un status--"}</option>
                            {validationStatus.map((choice) =>
                                <option key={choice['id']} value={choice['id']}>{choice['name']}</option>
                            )}
                          </select>
                        </label>
                        <p className='modality-subtitle'>Intervalle à respecter entre chaque application (jours) : </p>
                        <input className={(modalityFrequenceApplicationError === false) ? 'form-input' : 'form-input-error'} type='float' value={modalityFrequenceApplication} onChange={(e) => setModalityFrequenceApplication(e.target.value)}/>
                      </div>
                      <div>
                        <p className='modality-subtitle'>Conditions : </p>
                        <TextareaInput type='text' placeholder='Entrer les conditions de la modalité' value={modalityComment} setValue={setModalityComment} onKeyDown={handleKeyDown}/>
                      </div>
                      <div>
                        <button onClick={handleSubmit} className='button'>Valider</button>
                        <button onClick={handleCancel} className='button'>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    null
                  )}
                  {(modalities && modalities.length !== 0) ? (
                    modalities.sort((a, b) => (a.reference > b.reference)?1:-1).map((modality) => (
                      <Modality key={modality['id']} id={modality['id']} modality={modality} product_id={trial['product']['id']} product_version_name={trial["product_version"]} handleRemove={handleRemove} protocole={false} onModalityBlockChange={setModalityBlock}/>
                    ))
                  ) : (
                    <div><p className='table-cell-margin'>Aucune modalité actuellement. Veuillez en ajouter !</p></div>
                  )}
                </div>
                {/*Info de répétition (renommé en "Matériel et méthodes" sur l'interface)*/}
                <div className='margin-bottom'>
                  <div className={(screenWidth > 650) ? 'flex-row' : null}>
                    <h3 className='table-cell table-label'>Matériel et méthodes</h3>
                    <img src={EditIcon} alt='edit_item_icon' onClick={(e) => repeatEditIcon(e)} className='link-img link-icon' />
                  </div>
                  {(repeatUpdate === true) ? (
                    <form onSubmit={repeatUpdateSubmit} onReset={repeatUpdateReset} className='flex-row justify-end'>
                        <TextareaInput value={noteIntervalleRecurrence} setValue={setNoteIntervalleRecurrence} onKeyDown={handleKeyDown}/>
                        <button type="reset" className='button'>Annuler</button>
                        <button type="submit" className='button'>Valider</button>
                    </form> 
                  ) : (
                    (trial["note_intervalle_recurrence"] !== "") ? (
                      <p style={{ whiteSpace: 'pre-line' }} className='table-cell table-data'>{trial["note_intervalle_recurrence"]}</p>
                    ): (
                      <p className='table-cell table-data'>Aucune indication disponible.</p>
                    )
                  )}
                </div>
                {/*Conditions*/}
                <div className='margin-bottom'>
                  <h3 className='table-cell table-label'>Conditions</h3>
                  <div>
                    <div className='grid-1-4'>
                      <p className='modality-subtitle'>Lieu : </p>
                      {(locationUpdate === true) ? (
                        <form onSubmit={locationUpdateSubmit} onReset={locationUpdateReset} className='flex-row justify-end'>
                          <input className='form-input' value={location} onChange={(e) => setLocation(e.target.value)}/>
                          <button type="reset" className='button'>Annuler</button>
                          <button type="submit" className='button'>Valider</button>
                        </form> 
                      ) : (
                        <div className={(screenWidth > 650) ? 'flex-row' : null}>
                          <p className='modality-text'>{trial['location']}</p>
                          <img src={EditIcon} alt='edit_item_icon' onClick={(e) => locationEditIcon(e)} className='link-img link-icon' />
                        </div>
                      )}
                    </div>
                    <div className='grid-1-4'>
                      <p className='modality-subtitle'>Température ambiante : </p>
                      {(ambientTemperatureUpdate === true) ? (
                        <form onSubmit={ambientTemperatureUpdateSubmit} onReset={ambientTemperatureUpdateReset} className='flex-row justify-end'>
                          <input className='form-input' value={ambientTemperature} onChange={(e) => setAmbientTemperature(e.target.value)}/>
                          <button type="reset" className='button'>Annuler</button>
                          <button type="submit" className='button'>Valider</button>
                        </form> 
                      ) : (
                        <div className={(screenWidth > 650) ? 'flex-row' : null}>
                          <p className='modality-text'>{trial['ambient_temperature']}</p>
                          <img src={EditIcon} alt='edit_item_icon' onClick={(e) => ambientTemperatureEditIcon(e)} className='link-img link-icon' />
                        </div>
                      )}
                    </div>
                    <div className='grid-1-4'>
                      <p className='modality-subtitle'>Humidité ambiante : </p>
                      {(ambientHumidityUpdate === true) ? (
                        <form onSubmit={ambientHumidityUpdateSubmit} onReset={ambientHumidityUpdateReset} className='flex-row justify-end'>
                          <input className='form-input' value={ambientHumidity} onChange={(e) => setAmbientHumidity(e.target.value)}/>
                          <button type="reset" className='button'>Annuler</button>
                          <button type="submit" className='button'>Valider</button>
                        </form> 
                      ) : (
                        <div className={(screenWidth > 650) ? 'flex-row' : null}>
                          <p className='modality-text'>{trial['ambient_humidity']}</p>
                          <img src={EditIcon} alt='edit_item_icon' onClick={(e) => ambientHumidityEditIcon(e)} className='link-img link-icon' />
                        </div>
                      )}
                    </div>
                    <div className='grid-1-4'>
                      <p className='modality-subtitle'>Conditions de conservation : </p>
                      {(preservationConditionUpdate === true) ? (
                        <form onSubmit={preservationConditionUpdateSubmit} onReset={preservationConditionUpdateReset} className='flex-row justify-end'>
                          <input className='form-input' value={preservationCondition} onChange={(e) => setPreservationCondition(e.target.value)}/>
                          <button type="reset" className='button'>Annuler</button>
                          <button type="submit" className='button'>Valider</button>
                        </form> 
                      ) : (
                        <div className={(screenWidth > 650) ? 'flex-row' : null}>
                          <p className='modality-text'>{trial['preservation_condition']}</p>
                          <img src={EditIcon} alt='edit_item_icon' onClick={(e) => preservationConditionEditIcon(e)} className='link-img link-icon' />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/*Commentaires (renommé en 'Résultats' sur l'interface*/}
                <div className='margin-bottom'>
                  <div className={(screenWidth > 650) ? 'flex-row' : null}>
                    <h3 className='table-cell table-label'>Résultats</h3>
                    <img src={EditIcon} alt='edit_item_icon' onClick={(e) => commentEditIcon(e)} className='link-img link-icon' />
                  </div>
                  {(commentUpdate === true) ? (
                    <form onSubmit={commentUpdateSubmit} onReset={commentUpdateReset} className='flex-row justify-end'>
                        <TextareaInput value={comment} setValue={setComment} onKeyDown={handleKeyDown}/>
                        <button type="reset" className='button'>Annuler</button>
                        <button type="submit" className='button'>Valider</button>
                    </form> 
                  ) : (
                    (trial["comments"] !== "") ? (
                      <p style={{ whiteSpace: 'pre-line' }} className='table-cell table-data'>{trial["comments"]}</p>
                    ): (
                      <p className='table-cell table-data'>Aucun commentaire.</p>
                    )
                  )}
                </div>
                {/*Mots clés*/}
                <div className='margin-bottom'>
                  <div className={(screenWidth > 650) ? 'flex-row' : null}>
                    <h3 className='table-cell table-label'>Mots clés</h3>
                    <img src={EditIcon} alt='edit_item_icon' onClick={(e) => keywordsEditIcon(e)} className='link-img link-icon' />
                  </div>
                  {(keywordsUpdate === true) ? (
                    <form onSubmit={keywordsUpdateSubmit} onReset={keywordsUpdateReset} className='flex-row justify-end'>
                        <TextareaInput value={keywords} setValue={setKeywords} onKeyDown={handleKeyDown}/>
                        <button type="reset" className='button'>Annuler</button>
                        <button type="submit" className='button'>Valider</button>
                    </form> 
                  ) : (
                    (trial["keywords"] !== "") ? (
                      <p style={{ whiteSpace: 'pre-line' }} className='table-cell table-data'>{trial["keywords"]}</p>
                    ): (
                      <p className='table-cell table-data'>Aucun mot clé.</p>
                    )
                  )}
                </div>
                {/*Liste des articles scientifiques*/}
                <div className='margin-bottom margin-top'>
                  <div className='link'>
                    <h3 className='table-cell table-label'>Articles scientifiques  </h3>
                    <img src={AddIcon} alt='add_item_icon' onClick={handleClickArticleAdd} className='link-img link-icon' />
                    {(articleAdd === true) ? (
                      <label className='select-form'>
                        <select name='selectedChoiceToAdd' className='select-form-side-option' value={selectedArticleAdd} onChange={e => setSelectedArticleAdd(e.target.value)}>
                          <option value={0}>{'--Selectionnez un article à ajouter--'}</option>
                            {articleList.sort((a, b) => (a.author > b.author)?1:-1).map((choice) =>
                              <option key={choice['id']} value={choice['id']}>{choice['author']}{choice['published_year']}-{choice['title']} </option>
                            )}
                        </select>
                      </label>
                    ) : (
                      null
                    )}
                    <img src={RemoveIcon} alt='remove_item_icon' onClick={handleClickArticleRemove} className='link-img link-icon' />
                    {(articleRemove === true) ? (
                      <label className='select-form'>
                        <select name='selectedChoiceToRemove' className='select-form-side-option' value={selectedArticleRemove} onChange={e => setSelectedArticleRemove(e.target.value)}>
                          <option value={0}>{'--Selectionnez un article à supprimer--'}</option>
                            {articles.sort((a, b) => (a.scientific_article.author > b.scientific_article.author)?1:-1).map((choice) =>
                              <option key={choice['id']} value={choice['id']}>{choice["scientific_article"]['author']}{choice["scientific_article"]['published_year']}-{choice["scientific_article"]['title']}</option>
                            )}
                        </select>
                      </label>
                    ) : (
                      null
                    )}
                    <Tooltip className='link-img link-icon' 
                        title={
                            <p>Pour annuler l'ajout ou la suppression d'un article après avoir réaliser une saisie clavier
                               pour le retrouver dans la liste déroulante, ne pas cliquer en dehors de la liste déroulante.
                               Cela réalise l'action. 
                               La solution à utiliser pour éviter ce comportement est de recliquer sur le texte '--Sélectionner un article ...--' 
                               en haut de la liste déroulante puis fermer la liste en recliquant sur l'icône '+' ou '-' correspondante. </p>
                        }
                        arrow
                    >
                        <img src={HelpIcon} alt='help_icon' />
                    </Tooltip>
                  </div>
                  <div className='grid-auto'>
                    {(articles && articles.length !== 0) ? (
                      articles.sort((a, b) => (a.scientific_article.author > b.scientific_article.author)?1:-1).map((article, index) => (
                        <p key={index} className='stiker'>{article["scientific_article"]['author']}{article["scientific_article"]['published_year']}-{article["scientific_article"]['title']}</p>
                      ))
                    ) : (
                      <div><p>Aucun article scientifique actuellement. Veuillez en ajouter un !</p></div>
                    )}
                  </div>
                </div>
                {/*Liste des rapports internes*/}
                <div className='margin-bottom margin-top'>
                  <div className='link'>
                    <h3 className='table-cell table-label'>Rapports internes  </h3>
                    <img src={AddIcon} alt='add_item_icon' onClick={handleClickReportAdd} className='link-img link-icon' />
                    {(reportAdd === true) ? (
                      <label className='select-form'>
                        <select name='selectedChoice' className='select-form-side-option' value={selectedReportAdd} onChange={e => setSelectedReportAdd(e.target.value)}>
                            <option value={0}>{'--Selectionnez un rapport à ajouter--'}</option>
                            {reportList.sort((a, b) => (a.internal_reference > b.internal_reference)?1:-1).map((choice) =>
                                <option value={choice['id']}>{choice['internal_reference']}</option>
                            )}
                        </select>
                      </label>
                    ) : (
                      null
                    )}
                    <img src={RemoveIcon} alt='remove_item_icon' onClick={handleClickReportRemove} className='link-img link-icon' />
                    {(reportRemove === true) ? (
                      <label className='select-form'>
                        <select name='selectedChoiceToRemove' className='select-form-side-option' value={selectedReportRemove} onChange={e => setSelectedReportRemove(e.target.value)}>
                            <option value={0}>{'--Selectionnez un rapport à supprimer--'}</option>
                            {reports.map((choice) =>
                                <option value={choice['id']}>{choice["at_report"]["internal_reference"]}</option>
                            )}
                        </select>
                      </label>
                    ) : (
                      <div/>
                    )}
                    <Tooltip className='link-img link-icon' 
                        title={
                            <p>Pour annuler l'ajout ou la suppression d'un rapport après avoir réaliser une saisie clavier
                               pour le retrouver dans la liste déroulante, ne pas cliquer en dehors de la liste déroulante.
                               Cela réalise l'action. 
                               La solution à utiliser pour éviter ce comportement est de recliquer sur le texte '--Sélectionner un rapport ...--' 
                               en haut de la liste déroulante puis fermer la liste en recliquant sur l'icône '+' ou '-' correspondante. </p>
                        }
                        arrow
                    >
                        <img src={HelpIcon} alt='help_icon' />
                    </Tooltip>
                  </div>
                  <div className='flex-row'>
                    {(reports && reports.length !== 0) ? (
                      reports.sort((a, b) => (a.at_report.internal_reference > b.at_report.internal_reference)?1:-1).map((report, index) => (
                        <p key={index} className='stiker'>{report["at_report"]["internal_reference"]}</p>
                      ))
                    ) : (
                      <div><p>Aucun rapport interne actuellement. Veuillez en ajouter un !</p></div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <p>Essai non disponible !</p>
                {/*Lien de retour*/}
                <div className='flex-row justify-start margin-left margin-bottom margin-top'>
                    <div className='link-border'>
                        <BackPage path='/trials'>Retour à la liste des essais</BackPage>
                    </div>
                </div>
              </div>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default TrialDetails;
